// components/DeleteLeaveModal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Space,
  Alert,
} from "antd";
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DeleteFilled,
} from "@ant-design/icons";

/**
 * DeleteLeaveModal Component
 * Requires user to type a confirmation word before allowing deletion.
 * 
 * Props:
 * @param {boolean}  open          - Controls modal visibility
 * @param {function} onCancel      - Called when cancel is clicked or modal closed
 * @param {function} onConfirm     - Called when delete is confirmed
 * @param {string}   title         - Modal title (default: "លុបបេសកកម្ម")
 * @param {string}   confirmWord   - Word user must type (default: "DELETE")
 * @param {string}   recordName    - Name of record being deleted
 * @param {string}   recordDetail  - Additional detail (optional)
 * @param {string}   description   - Warning description text
 * @param {boolean}  loading       - Loading state for confirm button
 * @param {string}   okText        - Confirm button text (default: "លុប")
 * @param {string}   cancelText    - Cancel button text (default: "បោះបង់")
 */
const DeleteMissionModal = ({
  open = false,
  onCancel,
  onConfirm,
  title = "លុបបេសកកម្ម", // "Delete Mission"
  confirmWord = "DELETE",
  recordName = "",
  recordDetail = "",
  description = "សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ។ វានឹងលុបទិន្នន័យនេះជាអចិន្ត្រៃយ៍។",
  loading = false,
  okText = "លុប",
  cancelText = "បោះបង់",
}) => {
  const [confirmText, setConfirmText] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setConfirmText("");
    }
  }, [open]);

  // Handle confirm text change
  const handleConfirmTextChange = (e) => {
    setConfirmText(e.target.value);
  };

  // Check if typed text matches confirm word
  const isConfirmed = confirmText === confirmWord;

  // Handle confirm
  const handleConfirm = () => {
    if (isConfirmed && onConfirm) {
      onConfirm();
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setConfirmText("");
    if (onCancel) onCancel();
  };

  return (
    <Modal
      style={{ fontFamily: 'Content, sans-serif' }}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
          <span style={{ color: '#ff4d4f', fontFamily: 'Content, sans-serif' }}>{title}</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      closable={!loading}
      destroyOnClose
      footer={[
        <div 
          key="footer" 
          style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
        >
          <Button 
            onClick={handleCancel} 
            disabled={loading}
            style={{ fontFamily: 'Content, sans-serif' }}
          >
            {cancelText}
          </Button>
          <Button 
            onClick={handleConfirm} 
            type="primary" 
            danger
            disabled={!isConfirmed}
            icon={<DeleteFilled />}
            loading={loading}
            style={{ fontFamily: 'Content, sans-serif' }}
          >
            {okText}
          </Button>
        </div>,
      ]}
      width={520}
    >
      <div style={{ padding: '8px 0' }}>
        {/* Warning Alert */}
        <Alert
          message="ការព្រមាន"
          description={description}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16, fontFamily: 'Content, sans-serif' }}
        />
        
        {/* Record Info */}
        {recordName && (
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: 8, 
            padding: '12px 16px',
            marginBottom: 16 
          }}>
            <p style={{ margin: 0, color: '#333', fontSize: 14, fontFamily: 'Content, sans-serif' }}>
              តើអ្នកពិតជាចង់លុបបេសកកម្ម {" "}
              <strong style={{ color: '#ff4d4f' }}>"{recordName}"</strong> មែនទេ?
            </p>
            {recordDetail && (
              <p style={{ margin: '6px 0 0 0', color: '#666', fontSize: 12, fontFamily: 'Content, sans-serif' }}>
                {recordDetail}
              </p>
            )}
          </div>
        )}

        {/* Confirmation Input */}
        <div style={{ 
          background: '#fafafa', 
          border: '1px solid #d9d9d9', 
          borderRadius: 8, 
          padding: '16px',
        }}>
          <p style={{ 
            margin: '0 0 10px 0', 
            fontSize: 13, 
            color: '#555',
            fontWeight: 500,
            fontFamily: 'Content, sans-serif'
          }}>
            សូមបញ្ចូលពាក្យ{" "}
            <strong style={{ 
              color: '#ff4d4f', 
              background: '#fff2f0', 
              padding: '2px 8px', 
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 14,
              letterSpacing: 2
            }}>
              {confirmWord}
            </strong>{" "}
            ដើម្បីបញ្ជាក់ការលុប :
          </p>
          
          <Input
            placeholder={`សូមវាយបញ្ចូល "${confirmWord}" ដើម្បីបញ្ជាក់`}
            value={confirmText}
            onChange={handleConfirmTextChange}
            onPressEnter={handleConfirm}
            size="large"
            autoFocus
            disabled={loading}
            style={{ 
              fontFamily: 'monospace',
              letterSpacing: 2,
              fontWeight: 'bold'
            }}
            status={confirmText && !isConfirmed ? 'error' : ''}
            prefix={
              isConfirmed ? 
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            }
          />
          
          {/* Error Message */}
          {confirmText && !isConfirmed && (
            <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 8, marginBottom: 0, fontFamily: 'Content, sans-serif' }}>
              អក្សរមិនត្រឹមត្រូវទេ។ សូមវាយបញ្ចូល "{confirmWord}" ឲ្យបានត្រឹមត្រូវ
            </p>
          )}
          
          {/* Success Message */}
          {isConfirmed && (
            <p style={{ color: '#52c41a', fontSize: 12, marginTop: 8, marginBottom: 0, fontFamily: 'Content, sans-serif' }}>
              ការបញ្ជាក់ត្រឹមត្រូវ។ អ្នកអាចបន្តការលុបឥឡូវនេះ។
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DeleteMissionModal;